/**
 * Category visibility rule (single source of truth).
 *
 * Mirrors the desktop SubNavbar logic so the desktop sub-navbar and the mobile
 * navigation (drawer drill-down, pills) decide identically whether to show a
 * sub-category. A category is hidden when it is a "family", has no products, or
 * (for non-privileged / non-include-inactive views) has no active products.
 *
 * Note: `totalProducts === 0` / `activeProducts === 0` must hide. Only nullish
 * counts are treated as unknown and left visible.
 *
 *   isCategoryVisible(cat, role, includeInactive) -> boolean
 */
export const isCategoryVisible = (cat, role, includeInactive) => {
  if (!cat || cat.isFamily) return false;

  // totalProducts === 0 must hide the category; only treat nullish as "unknown → show".
  if (typeof cat.totalProducts === "number" && cat.totalProducts <= 0) {
    return false;
  }

  if (!includeInactive || !role) {
    if (typeof cat.activeProducts === "number" && cat.activeProducts <= 0) {
      return false;
    }
  }

  return true;
};

const findCategoryInTree = (nodes, slug) => {
  if (!nodes?.length || !slug) return null;
  for (const cat of nodes) {
    if (cat.slug === slug) return cat;
    const found = findCategoryInTree(cat.children, slug);
    if (found) return found;
  }
  return null;
};

/**
 * Next visible sibling under the same parent, or null when current is
 * top-level / last sibling (no cross-department advance).
 */
export const getNextVisibleSiblingCategory = (
  slug,
  categories,
  role,
  includeInactive,
) => {
  const current = findCategoryInTree(categories, slug);
  if (!current?.parentSlug) return null;

  const parent = findCategoryInTree(categories, current.parentSlug);
  if (!parent?.children?.length) return null;

  const siblings = parent.children.filter((cat) =>
    isCategoryVisible(cat, role, includeInactive),
  );
  const index = siblings.findIndex((cat) => cat.slug === slug);
  if (index < 0 || index >= siblings.length - 1) return null;
  return siblings[index + 1];
};
